import { Text } from "@chakra-ui/react";
import { Title } from "components/AcceptablePaymentMethods/AcceptablePaymentMethods.styles";
import { useLocale } from "hooks";
import { useUser } from "lib/user";
import React from "react";
import { colours } from "@shared/ui.design-system";
import type { BasketPricingType, Maybe } from "@shared/graphql.types";
import { fonts } from "theme/themeConfig";
import { OrderSections } from "./components";
import { DiscountsCode } from "./components/DiscountsCode";
import { useGetDiscountsByType } from "./components/hooks/useGetDiscountsByType";
import { ORDER_SUMMARY_CONSTANTS } from "./OrderSummary.constant";
import { Container, PriceBreakdownContainer } from "./OrderSummary.styles";

const {
  MEMBER_DISCOUNT,
  RETAIL_LABEL,
  SUBTOTAL,
  TOTAL,
  MEMBERSHIP_FEE,
  ESTIMATED_DELIVERY,
  SUBTOTAL_TAX,
  DELIVERY_METHOD_TAX,
  TRADITIONAL_RETAIL_PRICE
} = ORDER_SUMMARY_CONSTANTS;

type Props = {
  title?: string;
  isCheckout?: boolean;
  showDelivery?: boolean;
  pricing: BasketPricingType;
  deliveryMethod?: Maybe<string>;
  deliveryMethodName?: Maybe<string>;
  hasOnlyVirtualProducts: boolean;
  source?: "HCL" | "CT";
  showTotalPrice?: boolean;
};

export const OrderSummaryV2: React.FC<Props> = ({
  title,
  deliveryMethod,
  deliveryMethodName,
  showDelivery = false,
  isCheckout = false,
  hasOnlyVirtualProducts,
  source,
  pricing: {
    priceTier,
    totalProductPriceByTier,
    totalPrice,
    subTotal,
    estimatedShipping,
    shipping,
    totalMembershipPrice,
    totalDiscountsByType
  },
  showTotalPrice = true
}) => {
  const { basket, isMember } = useUser();
  const locale = useLocale();

  const priceType = locale === "en-US" ? "net" : "gross";
  const shippingPrice = shipping[priceType];
  const showEstimatedShippingCost = deliveryMethod === "None";
  const isGuestPrice = !isMember;

  let membersDiscountLabel: string | null = null;
  if (priceTier === "member") {
    membersDiscountLabel = MEMBER_DISCOUNT;
  }
  const totalBasketPrice =
    showEstimatedShippingCost && !hasOnlyVirtualProducts && basket.length
      ? totalPrice.gross + estimatedShipping
      : totalPrice.gross;

  // We have to show delivery method on order confirmation page and on basket/checkout if there are items
  const showDeliveryMethod =
    (!hasOnlyVirtualProducts && showDelivery) ||
    (!hasOnlyVirtualProducts && !showDelivery && basket.length > 0);

  const isHCL = source === "HCL";
  const showMembershipDiscount =
    totalProductPriceByTier.member > 0 && priceTier === "member";

  const showMembershipFee = totalMembershipPrice > 0;
  const deliveryMethodNameLabel = deliveryMethodName ?? "Delivery";

  const orderSections = {
    RETAIL_LABEL: {
      testId: "total-typical-price",
      label: (
        <Text variant="acuminLight" mt={0} flexGrow={1}>
          {`${RETAIL_LABEL} `}
          {!isGuestPrice && (
            <Text as="span" mt={0} color={colours.primary.dark}>
              {TRADITIONAL_RETAIL_PRICE}
            </Text>
          )}
        </Text>
      ),
      color: colours.primary.dark,
      variant: "acuminLight",
      price: totalProductPriceByTier.typical,
      hasLine: false,
      isActive: priceTier === "member" && !hasOnlyVirtualProducts
    },
    MEMBER_DISCOUNT: {
      testId: "member-savings",
      label: membersDiscountLabel,
      color: colours.primary.dark,
      variant: "acuminLight",
      price: totalProductPriceByTier.typical - totalProductPriceByTier.member,
      priceStyles: {
        textDecorationLine: priceTier === "member" ? "none" : "line-through"
      },
      customStyles: {
        display: "flex",
        width: "100%",
        justifyContent: "space-between",
        alignItems: "center"
      },
      signSymbol: "-",
      display: "inline",
      hasLine: false,
      isActive:
        (showMembershipDiscount && !isHCL) ||
        (showMembershipDiscount && isHCL && !hasOnlyVirtualProducts)
    },
    SUBTOTAL: {
      testId: "subtotal",
      label: SUBTOTAL,
      color: colours.primary.dark,
      variant: "acuminLight",
      price: totalProductPriceByTier[priceTier],
      hasLine: false,
      isActive: totalProductPriceByTier[priceTier] > 0
    },
    SUBTOTAL_TAX: {
      testId: "subtotalTax",
      label: SUBTOTAL_TAX,
      color: colours.primary.dark,
      variant: "acuminLight",
      price: subTotal.tax,
      hasLine: false,
      isActive: locale === "en-US" && subTotal.tax > 0
    },
    DELIVERY_METHOD: {
      testId: "delivery-method",
      label: showEstimatedShippingCost
        ? ESTIMATED_DELIVERY
        : deliveryMethodNameLabel,
      color: colours.primary.dark,
      variant: "acuminLight",
      price: showEstimatedShippingCost ? estimatedShipping : shippingPrice,
      hasLine: true,
      isActive: showDeliveryMethod
    },
    DELIVERY_METHOD_TAX: {
      testId: "delivery-method-tax",
      label: DELIVERY_METHOD_TAX,
      color: colours.primary.dark,
      variant: "acuminLight",
      price: shipping.tax,
      hasLine: false,
      isActive: locale === "en-US" && shipping.tax > 0
    },
    TOTAL: {
      testId: "total",
      label: TOTAL,
      color: colours.primary.dark,
      variant: "acuminSemiBold",
      price: totalBasketPrice,
      hasLine: true,
      isActive: showTotalPrice
    },

    MEMBERSHIP_FEE: {
      testId: "membership-fee",
      label: MEMBERSHIP_FEE,
      color: colours.primary.dark,
      variant: "acuminLight",
      price: totalMembershipPrice,
      hasLine: false,
      isActive:
        (showMembershipFee && !isHCL) ||
        (showMembershipFee && isHCL && !hasOnlyVirtualProducts)
    }
  };

  const { discounts, membershipDiscount } =
    useGetDiscountsByType(totalDiscountsByType);
  return (
    <Container spacing={1} {...(isCheckout ? { pt: "0" } : {})}>
      {title && (
        <Title
          as="h3"
          fontFamily={fonts.acuminSemiBold}
          fontSize={6}
          lineHeight={5}
        >
          {title}
        </Title>
      )}
      <PriceBreakdownContainer data-testid="pricing">
        <OrderSections
          display={orderSections.RETAIL_LABEL.isActive}
          section={orderSections.RETAIL_LABEL}
        />
        <OrderSections
          display={orderSections.MEMBER_DISCOUNT.isActive}
          section={orderSections.MEMBER_DISCOUNT}
        />
        <OrderSections
          display={orderSections.SUBTOTAL.isActive}
          section={orderSections.SUBTOTAL}
        />
        <OrderSections
          display={orderSections.SUBTOTAL_TAX.isActive}
          section={orderSections.SUBTOTAL_TAX}
        />

        <DiscountsCode discounts={discounts} />
        <OrderSections
          display={orderSections.DELIVERY_METHOD.isActive}
          section={orderSections.DELIVERY_METHOD}
        />
        {orderSections.DELIVERY_METHOD_TAX.price > 0 && (
          <OrderSections
            display={orderSections.DELIVERY_METHOD_TAX.isActive}
            section={orderSections.DELIVERY_METHOD_TAX}
          />
        )}
        <OrderSections
          display={orderSections.MEMBERSHIP_FEE.isActive}
          section={orderSections.MEMBERSHIP_FEE}
        />
        {membershipDiscount && (
          <DiscountsCode discounts={[membershipDiscount]} />
        )}

        <OrderSections
          display={orderSections.TOTAL.isActive}
          section={orderSections.TOTAL}
        />
      </PriceBreakdownContainer>
    </Container>
  );
};
